import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { caregiverProfileService } from '../../services/caregiverProfileService';

interface BioManagementSidebarProps {
  open: boolean;
  onClose: () => void;
  currentBio: string | null;
}

const BioManagementSidebar: React.FC<BioManagementSidebarProps> = ({
  open,
  onClose,
  currentBio,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [bio, setBio] = useState(currentBio || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      await caregiverProfileService.updateBio(bio);
      setSuccess(true);
      
      // Close sidebar after a short delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update bio');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (bio !== currentBio) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?'
      );
      if (!confirmed) return;
    }
    setBio(currentBio || '');
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleBioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBio(event.target.value);
    setError(null);
    setSuccess(false);
  };

  const isDirty = bio !== currentBio;
  const isValid = bio.length >= 10 && bio.length <= 2000;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : '40%',
          maxWidth: 600,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Edit Professional Bio</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Write a compelling bio that helps patients understand your expertise and approach to care.
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Bio updated successfully!
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={12}
            value={bio}
            onChange={handleBioChange}
            placeholder="Tell patients about your professional background, experience, and approach to care. This helps them understand why you're the right caregiver for them."
            variant="outlined"
            error={bio.length > 0 && !isValid}
            helperText={
              bio.length > 0 && !isValid
                ? 'Bio must be between 10 and 2000 characters'
                : `${bio.length}/2000 characters`
            }
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Tips for writing a great bio:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Start with your professional background and years of experience
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Mention your specialties and areas of expertise
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Describe your approach to patient care
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Include any unique qualifications or certifications
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Keep it professional but warm and approachable
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading || !isValid || !isDirty}
            >
              {loading ? 'Saving...' : 'Save Bio'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default BioManagementSidebar;
